import * as React from 'react'

import { BarcodeIcon, TruckIcon } from 'lucide-react'

import {
  Radio,
  RadioGroup,
} from '~/client/src/shared/components/shadcn-ui/radio-group'
import { GenericWorkflowIcon } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import './PermissionsGrid.scss'

interface IProps {
  value: { [key in Workflow]: Permission }
  onChange: (workflow: Workflow, permission: Permission) => void
}

enum Tool {
  Deliveries = 'deliveries',
  Forms = 'forms',
  ScannerApp = 'Scanner App',
}

function getToolTranslate(value: Tool) {
  switch (value) {
    case Tool.Deliveries:
      return Localization.translator.deliveries
    case Tool.Forms:
      return Localization.translator.workflows
    case Tool.ScannerApp:
      return Localization.translator.scannerApp
    default:
      throw new Error(value + ' is unhandled')
  }
}

export enum Workflow {
  DeliveryBooking = 'deliveryBooking',
  DeliveryInspection = 'deliveryInspection',
  Forms = 'forms',
  ScannerApp = 'scannerApp',
}

function getWorkflowTranslate(value: Workflow) {
  switch (value) {
    case Workflow.DeliveryBooking:
      return Localization.translator.projectWorkflows.deliveryBooking
    case Workflow.DeliveryInspection:
      return Localization.translator.projectWorkflows.deliveryInspection
    case Workflow.Forms:
      return Localization.translator.projectWorkflows.formsMaster
    case Workflow.ScannerApp:
      return Localization.translator.projectWorkflows.scanMaster
    default:
      throw new Error(value + ' is unhandled')
  }
}

export enum Permission {
  None = 'none',
  ReadOnly = 'readOnly',
  Standard = 'standard',
  Master = 'master',
}

function getPermissionTranslate(value: Permission) {
  switch (value) {
    case Permission.Master:
      return Localization.translator.permissions.master
    case Permission.None:
      return Localization.translator.permissions.none
    case Permission.ReadOnly:
      return Localization.translator.permissions.readOnly
    case Permission.Standard:
      return Localization.translator.permissions.standard
    default:
      throw new Error(value + ' is unhandled')
  }
}

interface IPermissionConfiguration {
  tool: Tool
  icon: JSX.Element
  workflows: Workflow[]
}

const configuration: IPermissionConfiguration[] = [
  {
    tool: Tool.Deliveries,
    icon: <TruckIcon size={10} className="tw:mx-auto tw:mt-[5px] tw:block" />,
    workflows: [Workflow.DeliveryBooking, Workflow.DeliveryInspection],
  },
  {
    tool: Tool.Forms,
    icon: <GenericWorkflowIcon className="tw:flex-none! tw:pt-[5px]" />,
    workflows: [Workflow.Forms],
  },
  {
    tool: Tool.ScannerApp,
    icon: (
      <span className="row pt5">
        <BarcodeIcon size={16} />
      </span>
    ),
    workflows: [Workflow.ScannerApp],
  },
]

export default class PermissionsGrid extends React.Component<IProps> {
  public static defaultProps = {
    value: {},
  }

  public render() {
    const { value, onChange } = this.props

    return (
      <div className="col permissions-grid mx24">
        <div className="row text bold uppercase lp05">
          <div className="tool-cell">{Localization.translator.tool}</div>
          <div className="workflow-cell">
            {Localization.translator.workflow}
          </div>

          {Object.values(Permission).map(perm => (
            <div key={perm} className="text center">
              {getPermissionTranslate(perm)}
            </div>
          ))}
        </div>

        <div className="my12 ba-none bb-brand-dark as-stretch" />

        {configuration.map(({ tool, workflows, icon }) => {
          return (
            <React.Fragment key={tool}>
              <div className="row">
                <div className="row no-grow tool-cell">
                  <div className="tool-icon-wrapper br-rounded mr8">{icon}</div>
                  <div className="no-grow">{getToolTranslate(tool)}</div>
                </div>

                <div className="col">
                  {workflows.map((workflow, i) => {
                    const isLast = i === workflows.length - 1
                    const currentPermission =
                      value[workflow] || Permission.Standard
                    return (
                      <React.Fragment key={workflow}>
                        <RadioGroup
                          className="row"
                          value={currentPermission}
                          onValueChange={value =>
                            onChange(workflow, value as Permission)
                          }
                        >
                          <div className="workflow-cell">
                            {getWorkflowTranslate(workflow)}
                          </div>
                          <div className="tw:flex tw:justify-center">
                            <Radio value={Permission.None} disabled={true} />
                          </div>
                          <div className="tw:flex tw:justify-center">
                            <Radio
                              value={Permission.ReadOnly}
                              disabled={true}
                            />
                          </div>
                          <div className="tw:flex tw:justify-center">
                            <Radio value={Permission.Standard} />
                          </div>
                          <div className="tw:flex tw:justify-center">
                            <Radio value={Permission.Master} />
                          </div>
                        </RadioGroup>
                        {!isLast && (
                          <div className="my12 ba-none bb-light-grey as-stretch" />
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>
              </div>
              <div className="my12 ba-none bb-light-grey as-stretch" />
            </React.Fragment>
          )
        })}
      </div>
    )
  }
}
